// A small row of three icon buttons — Save / New / Import — shared by the Model
// and Layout tool sidebars (the desktop "toolkit"). The `noun` ("model" /
// "layout") only sets the tooltips; each mode injects its own behaviour, so the
// same row does model ops in Model mode and layout ops in Layout mode.

let _injected = false;
function inject(): void {
  if (_injected) return;
  _injected = true;
  const s = document.createElement("style");
  // Scoped under the two sidebar ids so these beat the sidebars' own
  // higher-specificity `#dt-ui button` / `#fp-ui button` rules.
  s.textContent = `
.dt-actbar { display: flex; gap: 6px; }
#dt-ui .dt-actbar button, #fp-ui .dt-actbar button {
  position: relative; flex: 1; height: 34px; padding: 7px; box-sizing: border-box;
  border-radius: 8px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; border: 1px solid rgba(255,255,255,0.14);
  background: rgba(255,255,255,0.05); color: #cdd2de;
  transition: background .15s ease, border-color .15s ease, color .15s ease,
    transform .08s ease;
}
#dt-ui .dt-actbar button:hover, #fp-ui .dt-actbar button:hover {
  background: rgba(255,255,255,0.12); color: #fff;
}
/* Tactile press: the button dips slightly while held (release commits the
   action; dragging off and releasing fires no click — see action-bar.ts). */
#dt-ui .dt-actbar button:active, #fp-ui .dt-actbar button:active {
  transform: scale(0.92);
}
/* Saved confirmation: the Save icon briefly becomes a checkmark that pops in,
   then reverts. No colour wash — the icon swap + the unsaved dot clearing are
   the feedback. */
#dt-ui .dt-actbar button.saved, #fp-ui .dt-actbar button.saved { color: #fff; }
#dt-ui .dt-actbar button.saved svg, #fp-ui .dt-actbar button.saved svg {
  animation: dt-actbar-check .32s cubic-bezier(.2, .8, .3, 1) both;
}
@keyframes dt-actbar-check {
  0%   { transform: scale(.4);  opacity: 0; }
  55%  { transform: scale(1.18); opacity: 1; }
  100% { transform: scale(1);   opacity: 1; }
}
/* Small dot marking unsaved changes on the Save button. */
#dt-ui .dt-actbar button.unsaved::after, #fp-ui .dt-actbar button.unsaved::after {
  content: ''; position: absolute; top: 4px; right: 4px;
  width: 6px; height: 6px; border-radius: 50%; background: #ffb454;
}
.dt-actbar svg { width: 100%; height: 100%; display: block; }
`;
  document.head.appendChild(s);
}

// Inline (stroke = currentColor) icons so they sit monochrome on the dark UI.
const SVG = (inner: string): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ` +
  `stroke-linecap="round" stroke-linejoin="round">${inner}</svg>`;
const ICON_SAVE = SVG(
  '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>' +
    '<polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>',
);
// Shown briefly in place of the Save icon to confirm a save (modern "saved ✓").
const ICON_CHECK = SVG('<polyline points="20 6 9 17 4 12"/>');
// Save-as: a save box with a small "+" in the corner (save to a new entry).
const ICON_SAVE_AS = SVG(
  '<path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v4"/>' +
    '<polyline points="7 3 7 8 15 8"/>' +
    '<line x1="19" y1="14" x2="19" y2="20"/><line x1="16" y1="17" x2="22" y2="17"/>',
);
const ICON_NEW = SVG(
  '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>' +
    '<polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/>' +
    '<line x1="9" y1="15" x2="15" y2="15"/>',
);
// Upload (arrow up out of a tray) — distinct from the thumbnails' download/export arrows.
const ICON_UPLOAD = SVG(
  '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>' +
    '<polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>',
);

export interface ActionBar {
  el: HTMLElement;
  flashSaved(): void; // briefly flash the Save button green (after a save)
  setUnsaved(dirty: boolean): void; // show/hide the unsaved dot on Save
}

export function createActionBar(opts: {
  noun: string; // "model" | "layout" — tooltips only
  onSave: () => void;
  onSaveAs: () => void;
  onNew: () => void;
  onImport: () => void;
}): ActionBar {
  inject();
  const bar = document.createElement("div");
  bar.className = "dt-actbar";
  let flashTimer = 0;
  const mk = (svg: string, title: string, cb: () => void): HTMLButtonElement => {
    const b = document.createElement("button");
    b.type = "button";
    b.title = title;
    b.innerHTML = svg;
    b.addEventListener("click", cb);
    bar.appendChild(b);
    return b;
  };
  // The save commits on the CLICK — i.e. on release over the button (which also
  // covers keyboard activation). Press down, drag off, and release elsewhere
  // fires no click, so nothing saves. The pressed/hover look is pure CSS
  // (:active / :hover); confirmation is the checkmark swap in flashSaved.
  const saveBtn = mk(ICON_SAVE, `Save ${opts.noun}`, opts.onSave);

  mk(ICON_SAVE_AS, `Save ${opts.noun} as…`, opts.onSaveAs);
  mk(ICON_NEW, `New ${opts.noun}`, opts.onNew);
  mk(ICON_UPLOAD, `Import ${opts.noun}`, opts.onImport);

  return {
    el: bar,
    flashSaved(): void {
      // Briefly swap the Save icon for a checkmark that pops in, then revert —
      // the quiet "saved ✓" confirmation modern apps use, no colour wash.
      saveBtn.innerHTML = ICON_CHECK;
      saveBtn.classList.add("saved");
      clearTimeout(flashTimer);
      flashTimer = window.setTimeout(() => {
        saveBtn.classList.remove("saved");
        saveBtn.innerHTML = ICON_SAVE;
      }, 900);
    },
    setUnsaved(dirty: boolean): void {
      saveBtn.classList.toggle("unsaved", dirty);
    },
  };
}
